export enum UsageLogLineErrorCode {
  USAGE_LOG_QUANTITY_INVALID = 'USAGE_LOG_QUANTITY_INVALID',
  USAGE_LOG_DATE_INVALID = 'USAGE_LOG_DATE_INVALID',
  USAGE_LOG_ENTITY_DIFFERENT_IN_DOCUMENT = 'USAGE_LOG_ENTITY_DIFFERENT_IN_DOCUMENT',
  USAGE_LOG_CURRENCY_DIFFERENT_IN_DOCUMENT = 'USAGE_LOG_CURRENCY_DIFFERENT_IN_DOCUMENT',
  USAGE_LOG_CONTRACT_PO_DIFFERENT_IN_DOCUMENT = 'USAGE_LOG_CONTRACT_PO_DIFFERENT_IN_DOCUMENT',
  CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND',
  SUPPLIER_NOT_FOUND = 'SUPPLIER_NOT_FOUND',
  CURRENCY_NOT_FOUND = 'CURRENCY_NOT_FOUND',
  USAGE_PRODUCT_NOT_FOUND = 'USAGE_PRODUCT_NOT_FOUND',
  USAGE_LOG_DOCUMENT_NOT_FOUND = 'USAGE_LOG_DOCUMENT_NOT_FOUND',
}

export enum UsageEventErrorCode {
  REQUEST_NOT_FOUND = 'REQUEST_NOT_FOUND',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
}

export enum BaseErrorCode {
  /** Foreign Key Not Found **/
  // Can't create a new object because one of its foreign key IDs not found in the DB
  FOREIGN_KEY_NOT_FOUND = 'FOREIGN_KEY_NOT_FOUND',
  // Can't create a new usage report because its usage product ID not found in the DB
  REPORT_PRODUCT_NOT_FOUND = 'REPORT_PRODUCT_NOT_FOUND',

  /** Foreign Key Restriction **/
  // Not allowed to delete a usage report related to an invoice
  FOREIGN_KEY_RESTRICTION = 'FOREIGN_KEY_RESTRICTION',
  // Not allowed to delete a usage report related to an invoice
  REPORT_INVOICE_RESTRICTION = 'REPORT_INVOICE_RESTRICTION',
  // Not allowed to delete a usage product that is used by a pricing model
  PRICING_MODEL_USAGE_PRODUCT_RESTRICTION = 'PRICING_MODEL_USAGE_PRODUCT_RESTRICTION',
  // Not allowed to delete an invoice with usage reports
  USAGE_REPORT_INVOICE_RESTRICTION = 'USAGE_REPORT_INVOICE_RESTRICTION',
  // Not allowed to delete a credit note with usage reports
  USAGE_REPORT_CREDIT_NOTE_RESTRICTION = 'USAGE_REPORT_CREDIT_NOTE_RESTRICTION',
  // Not allowed to delete entity used in pricing model
  PRICING_MODEL_ENTITY_RESTRICTION = 'PRICING_MODEL_ENTITY_RESTRICTION',

  /** Unique Restriction **/
  // Invoice number must be unique per tenant
  INVOICE_NUMBER_TENANT_UQ = 'INVOICE_NUMBER_TENANT_UQ',

  UQ_USAGE_PRODUCT_NAME = 'UQ_USAGE_PRODUCT_NAME',

  UQ_COMPANY_NAME = 'UQ_COMPANY_NAME',

  CONTRACT_CUSTOMER_USAGE_DATES_OVERLAP = 'CONTRACT_CUSTOMER_USAGE_DATES_OVERLAP',

  UQ_TENANT_STRIPE_ACCOUNT = 'UQ_TENANT_STRIPE_ACCOUNT',

  IDX_CUSTOMER_REMOTE_ID = 'IDX_CUSTOMER_REMOTE_ID',

  /** Business Logic Restriction Errors **/
  // Not allowed to delete an invoice in the following states: Sent, Paid, Late
  ACTIVE_INVOICE_RESTRICTION = 'ACTIVE_INVOICE_RESTRICTION',
  // Not allowed to delete a contract with an invoice in the following states: Sent, Paid, Late
  CONTRACT_ACTIVE_INVOICE_RESTRICTION = 'CONTRACT_ACTIVE_INVOICE_RESTRICTION',
  // Not allowed to delete a customer with an invoice in the following states: Sent, Paid, Late
  CUSTOMER_ACTIVE_INVOICE_RESTRICTION = 'CUSTOMER_ACTIVE_INVOICE_RESTRICTION',

  INVOICE_ALREADY_PAID = 'INVOICE_ALREADY_PAID',

  /** Application Errors **/
  // One of the cells in the pricing table is missing data
  PRICING_TABLE_MISSING_DATA = 'PRICING_TABLE_MISSING_DATA',
  // One of the formulas cannot be calculated
  PRICING_TABLE_CALCULATION_ERROR = 'PRICING_TABLE_CALCULATION_ERROR',
  // The usage report period does not match any invoice period
  INVALID_USAGE_REPORT_PERIOD = 'INVALID_USAGE_REPORT_PERIOD',
  // No invoice found for the usage report period
  NO_INVOICE_FOR_USAGE_REPORT_PERIOD = 'NO_INVOICE_FOR_USAGE_REPORT_PERIOD',
  // No documents found for the usage report period
  NO_DOCUMENTS_FOR_USAGE_REPORT_PERIOD = 'NO_DOCUMENTS_FOR_USAGE_REPORT_PERIOD',
  // Invoices for usage report period are in invalid state
  USAGE_REPORT_INVOICES_IN_INVALID_STATE = 'USAGE_REPORT_INVOICES_IN_INVALID_STATE',
  // Documents for usage report period are in invalid state
  USAGE_REPORT_DOCUMENTS_IN_INVALID_STATE = 'USAGE_REPORT_DOCUMENTS_IN_INVALID_STATE',
  // Default supplier is not defined
  DEFAULT_SUPPLIER_NOT_DEFINED = 'DEFAULT_SUPPLIER_NOT_DEFINED',
  // Settings not defined
  SETTINGS_NOT_FOUND = 'SETTINGS_NOT_FOUND',
  // Settings not editable
  SETTING_NOT_EDITABLE = 'SETTING_NOT_EDITABLE',
  // Not allowed to update non-manual invoice number
  INVOICE_NUMBER_UPDATE_NOT_ALLOWED = 'INVOICE_NUMBER_UPDATE_NOT_ALLOWED',
  // Invalid number for manual invoice number assignment, only numbers lower than the starting number can be used
  INVALID_MANUAL_INVOICE_NUMBER = 'INVALID_MANUAL_INVOICE_NUMBER',
  // Invalid number for manual invoice number assignment, the number is already in use
  INVOICE_NUMBER_ALREADY_IN_USE = 'INVOICE_NUMBER_ALREADY_IN_USE',
  // Not allowed to update a submitted invoice (state >= SENT)
  SENT_INVOICE_UPDATE_NOT_ALLOWED = 'SENT_INVOICE_UPDATE_NOT_ALLOWED',
  // Not allowed to update a locked invoice
  LOCKED_INVOICE_UPDATE_NOT_ALLOWED = 'LOCKED_INVOICE_UPDATE_NOT_ALLOWED',
  // Not allowed to split a submitted invoice (state >= SENT)
  SENT_INVOICE_SPLIT_NOT_ALLOWED = 'SENT_INVOICE_SPLIT_NOT_ALLOWED',
  // Not allowed to edit default pricing model templates
  PRICING_MODEL_TEMPLATE_UPDATE_NOT_ALLOWED = 'PRICING_MODEL_TEMPLATE_UPDATE_NOT_ALLOWED',
  // Not allowed to delete default pricing model templates
  PRICING_MODEL_TEMPLATE_DELETE_NOT_ALLOWED = 'PRICING_MODEL_TEMPLATE_DELETE_NOT_ALLOWED',
  // Not allowed to update a contract with a sent document
  CONTRACT_UPDATE_WITH_SENT_DOCUMENT_NOT_ALLOWED = 'CONTRACT_UPDATE_WITH_SENT_DOCUMENT_NOT_ALLOWED',
  // Not allowed to update a contract with a document with usage reports
  CONTRACT_UPDATE_WITH_DOCUMENT_WITH_USAGE_REPORTS_NOT_ALLOWED = 'CONTRACT_UPDATE_WITH_DOCUMENT_WITH_USAGE_REPORTS_NOT_ALLOWED',
  // Not allowed to update the billing end date to be before the billing start date of an active document
  CONTRACT_UPDATE_BILLING_END_DATE_BEFORE_ACTIVE_DOCUMENT_NOT_ALLOWED = 'CONTRACT_UPDATE_BILLING_END_DATE_BEFORE_ACTIVE_DOCUMENT_NOT_ALLOWED',
  // Not allowed to update the billing end date of an active one time document
  CONTRACT_UPDATE_BILLING_END_DATE_ACTIVE_ONE_TIME_DOCUMENT_NOT_ALLOWED = 'CONTRACT_UPDATE_BILLING_END_DATE_ACTIVE_ONE_TIME_DOCUMENT_NOT_ALLOWED',
  // Not allowed to update the billing start date of an ongoing contract
  CONTRACT_UPDATE_BILLING_START_DATE_NOT_ALLOWED_ON_ONGOING = 'CONTRACT_UPDATE_BILLING_START_DATE_NOT_ALLOWED_ON_ONGOING',
  // Not allowed to update a contract that is not active
  CONTRACT_UPDATE_NOT_ACTIVE_NOT_ALLOWED = 'CONTRACT_UPDATE_NOT_ACTIVE_NOT_ALLOWED',
  // Every code below names what was refused, not the state that refused it, so a caller reading one knows
  // which request to change. Each value is matched by name in Stigg (CALLER_FIXABLE_BILLING_ERROR_CODES),
  // so key and value are kept identical and renaming one is a paired change across both services.

  // The contract is active and can no longer be modified — the operation is only valid on a draft
  CONTRACT_MODIFICATION_NOT_ALLOWED_ON_ACTIVE = 'CONTRACT_MODIFICATION_NOT_ALLOWED_ON_ACTIVE',
  // A pay-as-you-go pricing model was used for an item that is not metered, so no usage can resolve its
  // quantity
  PAY_AS_YOU_GO_NOT_ALLOWED_FOR_UNMETERED_ITEM = 'PAY_AS_YOU_GO_NOT_ALLOWED_FOR_UNMETERED_ITEM',
  // Contract-wide terms or settings were applied to a contract that has no line items to carry them
  CONTRACT_UPDATE_NOT_ALLOWED_WITHOUT_LINE_ITEMS = 'CONTRACT_UPDATE_NOT_ALLOWED_WITHOUT_LINE_ITEMS',
  // Contract-wide payment terms were applied to a contract whose line items are all Separate-Invoice, so
  // there is no collective item to carry them and each bills on its own terms
  CONTRACT_PAYMENT_TERMS_NOT_ALLOWED_WITHOUT_COLLECTIVE_ITEM = 'CONTRACT_PAYMENT_TERMS_NOT_ALLOWED_WITHOUT_COLLECTIVE_ITEM',
  // A quantity was set on a pay-as-you-go line item, whose quantity comes from reported usage
  QUANTITY_NOT_ALLOWED_FOR_PAY_AS_YOU_GO = 'QUANTITY_NOT_ALLOWED_FOR_PAY_AS_YOU_GO',
  // An in-advance charge type was paired with pay as you go, which can only bill in arrears
  CHARGE_TYPE_NOT_ALLOWED_FOR_PAY_AS_YOU_GO = 'CHARGE_TYPE_NOT_ALLOWED_FOR_PAY_AS_YOU_GO',
  // A line item was added in a currency the contract does not bill in
  LINE_ITEM_CURRENCY_NOT_ALLOWED_ON_CONTRACT = 'LINE_ITEM_CURRENCY_NOT_ALLOWED_ON_CONTRACT',
  // A line item row carried a value that is not a number
  LINE_ITEM_ROW_VALUE_INVALID = 'LINE_ITEM_ROW_VALUE_INVALID',
  // Publishing was refused by invoice generation — the contract is missing something invoices need
  CONTRACT_PUBLISH_REJECTED = 'CONTRACT_PUBLISH_REJECTED',
  // Both are capability signals rather than failures: the caller degrades to its own estimate instead of
  // surfacing an error, so anything genuinely broken must NOT be reported with these.
  //
  // A preview was asked for with no contract to price against, on a build that cannot cost a
  // subscription that does not exist yet. Reserved: the preview endpoint does support that case, so
  // nothing emits this today — it exists because the caller branches on it.
  PREVIEW_REQUIRES_CONTRACT = 'PREVIEW_REQUIRES_CONTRACT',
  // The rows cannot be priced as asked — today, a billing cadence Received's proration arithmetic
  // cannot express (weekly or custom).
  PREVIEW_NOT_SUPPORTED_FOR_CONTRACT = 'PREVIEW_NOT_SUPPORTED_FOR_CONTRACT',
  // Billing settings named a legal entity or a payment account that does not exist on this tenant
  CONTRACT_LEGAL_ENTITY_NOT_FOUND = 'CONTRACT_LEGAL_ENTITY_NOT_FOUND',
  CONTRACT_PAYMENT_ACCOUNT_NOT_FOUND = 'CONTRACT_PAYMENT_ACCOUNT_NOT_FOUND',
  // Not allowed to update a usage report with an sent invoice
  USAGE_REPORT_UPDATE_WITH_SEND_INVOICE_NOT_ALLOWED = 'USAGE_REPORT_UPDATE_WITH_SEND_INVOICE_NOT_ALLOWED',
  // Not allowed to delete a usage report with an sent invoice
  USAGE_REPORT_DELETE_WITH_SEND_INVOICE_NOT_ALLOWED = 'USAGE_REPORT_UPDATE_WITH_SEND_INVOICE_NOT_ALLOWED',
  // Not allowed to update a usage report related to a sent document
  USAGE_REPORT_UPDATE_WITH_SENT_DOCUMENT_NOT_ALLOWED = 'USAGE_REPORT_UPDATE_WITH_SENT_DOCUMENT_NOT_ALLOWED',
  // Not allowed to delete a usage report related to a sent document
  USAGE_REPORT_DELETE_WITH_SENT_DOCUMENT_NOT_ALLOWED = 'USAGE_REPORT_DELETE_WITH_SENT_DOCUMENT_NOT_ALLOWED',
  // Not allowed to update a paid credit note (state >= PAID)
  PAID_CREDIT_NOTE_UPDATE_NOT_ALLOWED = 'PAID_CREDIT_NOTE_UPDATE_NOT_ALLOWED',
  // Not allowed to delete a paid credit note (state >= PAID)
  PAID_CREDIT_NOTE_DELETE_NOT_ALLOWED = 'PAID_CREDIT_NOTE_DELETE_NOT_ALLOWED',
  // Not allowed to update a credit note that is used by sent invoices
  CREDIT_NOTE_UPDATE_WITH_SENT_INVOICE_NOT_ALLOWED = 'CREDIT_NOTE_UPDATE_WITH_SENT_INVOICE_NOT_ALLOWED',
  // Not allowed to delete a credit note that is used by sent invoices
  CREDIT_NOTE_DELETE_WITH_SENT_INVOICE_NOT_ALLOWED = 'CREDIT_NOTE_DELETE_WITH_SENT_INVOICE_NOT_ALLOWED',
  // Customer is missing in contract
  MISSING_CUSTOMER_IN_CONTRACT = 'MISSING_CUSTOMER_IN_CONTRACT',
  // Billing cycle not supported
  BILLING_CYCLE_NOT_SUPPORTED = 'BILLING_CYCLE_NOT_SUPPORTED',
  // Failed to send an invoice
  FAILED_TO_SEND_INVOICE = 'FAILED_TO_SEND_INVOICE',
  FAILED_TO_SEND_CREDIT = 'FAILED_TO_SEND_CREDIT',
  // Pricing model is required to generate a PDF
  PRICING_MODEL_REQUIRED = 'PRICING_MODEL_REQUIRED',
  // Referral fee is supported only for partner customers
  REFERRAL_FEE_NOT_SUPPORTED = 'REFERRAL_FEE_NOT_SUPPORTED',
  // Period start or period end is missing
  INVALID_REV_REC_PERIOD = 'INVALID_REV_REC_PERIOD',
  // recognitionAmount is required for the revenue recognition method and it is missing
  MISSING_REV_REC_AMOUNT = 'MISSING_REV_REC_AMOUNT',

  /** Integration Errors **/
  // Integration not found
  INTEGRATION_NOT_FOUND = 'INTEGRATION_NOT_FOUND',
  // Integration not connected
  INTEGRATION_NOT_CONNECTED = 'INTEGRATION_NOT_CONNECTED',
  // Operation is not allowed on an integration
  INTEGRATION_NOT_SUPPORTED = 'INTEGRATION_NOT_SUPPORTED',
  // Integration connection not allowed
  INTEGRATION_CONNECTION_NOT_ALLOWED = 'INTEGRATION_CONNECTION_NOT_ALLOWED',
  // Failed to push a customer to the accounting platform
  CUSTOMER_PUSH_FAILURE = 'CUSTOMER_PUSH_FAILURE',
  // Failed to push an invoice to the accounting platform
  INVOICE_PUSH_FAILURE = 'INVOICE_PUSH_FAILURE',
  // Failed to push a billing account to the accounting platform
  BILLING_ACCOUNT_PUSH_FAILURE = 'BILLING_ACCOUNT_PUSH_FAILURE',
  // Failed to push a product to the accounting platform
  PRODUCT_PUSH_FAILURE = 'PRODUCT_PUSH_FAILURE',
  // Failed to sync products because no billing account is defined in the accounting platform
  PRODUCT_PUSH_BILLING_ACCOUNT_UNDEFINED = 'PRODUCT_PUSH_BILLING_ACCOUNT_UNDEFINED',
  // Accounting account is already connected
  ACCOUNTING_CONNECTED = 'ACCOUNTING_CONNECTED',
  // Accounting account is not connected
  ACCOUNTING_NOT_CONNECTED = 'ACCOUNTING_NOT_CONNECTED',
  // Accounting account is not connected
  ACCOUNTING_CONNECTION_MISSING = 'ACCOUNTING_CONNECTION_MISSING',
  // Accounting account is not connected
  BILLING_ACCOUNT_MISSING = 'BILLING_ACCOUNT_MISSING',
  // No sync status found for the entity
  ENTITY_NOT_SYNCED = 'ENTITY_NOT_SYNCED',
  // Accounting account does not exist
  NO_ACCOUNTING_EXISTS = 'NO_ACCOUNTING_EXISTS',
  // Failed to delete an account
  FAILED_TO_DELETE_ACCOUNT = 'FAILED_TO_DELETE_ACCOUNT',
  // Accounting API error
  ACCOUNTING_API_ERROR = 'ACCOUNTING_API_ERROR',
  // can't use the same item price in more than one price model
  ITEM_PRICE_USED_IN_MORE_THAN_ONE_PRICE_MODEL = 'ITEM_PRICE_USED_IN_MORE_THAN_ONE_PRICE_MODEL',

  // user can't delete himself
  SELF_DELETE_NOT_ALLOWED = 'SELF_DELETE_NOT_ALLOWED',

  // customers country not defined
  CUSTOMER_COUNTRY_NOT_DEFINED = 'CUSTOMER_COUNTRY_NOT_DEFINED',
  // country not mapped in date calculator
  COUNTRY_NAME_NOT_MAPPED = 'COUNTRY_NAME_NOT_MAPPED',

  // can't edit default db objects
  DEFAULT_OBJECT_EDIT_NOT_ALLOWED = 'DEFAULT_OBJECT_EDIT_NOT_ALLOWED',

  OBJECT_MAPPER_NOT_FOUND = 'OBJECT_MAPPER_NOT_FOUND',
  CSV_HEADER_NOT_FOUND = 'CSV_HEADER_NOT_FOUND',
  OBJECT_MAPPER_MAPPING_INVALID = 'OBJECT_MAPPER_MAPPING_INVALID',
  USAGE_LOG_CSV_NOT_FOUND = 'USAGE_LOG_CSV_NOT_FOUND',

  USAGE_LOG_FORMULA_HEADER_NOT_FOUND = 'USAGE_LOG_FORMULA_HEADER_NOT_FOUND',

  COLLECTIVE_INVOICE_DIFFERENT_BILLING_DATES = 'COLLECTIVE_INVOICE_DIFFERENT_BILLING_DATES',

  PRICING_DUNNING_SEQUENCE_RESTRICTION = 'PRICING_DUNNING_SEQUENCE_RESTRICTION',
  DUNNING_SEQUENCE_DEFAULT_DELETE_NOT_ALLOWED = 'DUNNING_SEQUENCE_DEFAULT_DELETE_NOT_ALLOWED',

  /** Unmapped **/
  UNMAPPED_ERROR = 'UNMAPPED_ERROR',
}

export type ErrorCode = BaseErrorCode | UsageLogLineErrorCode | UsageEventErrorCode;
export const ErrorCode = { ...BaseErrorCode, ...UsageLogLineErrorCode };
